// What changed on an Amazon person's own items since their last login: a line under the
// title counting each kind of change, each a toggle that filters the list to the items it
// counts (docs/Y2026-Q4/design-amazon-item-owners.md section 2.10). Dismissed until the
// next login, which forgets the filter too; not shown when nothing changed, nor to InRange.
// Each count is of the latest change on items the list shows under its other filters.
import type { ItemChange } from "../../api/hooks/useAmazonOwners";
import { formatDate } from "./dates";
import styles from "./sinceLastLogin.module.css";

export const SinceLastLogin = ({
  since,
  latest,
  picked,
  onPick,
  onDismiss,
}: {
  since: string;
  /** Each changed item's latest change. */
  latest: ItemChange[];
  /** The kinds the list is filtered to; none, unfiltered. */
  picked: ReadonlySet<string>;
  onPick: (next: Set<string>) => void;
  onDismiss: () => void;
}) => {
  if (latest.length === 0) return null;
  const counts = new Map<string, { label: string; n: number }>();
  for (const c of latest) {
    counts.set(c.kind, { label: c.label, n: (counts.get(c.kind)?.n ?? 0) + 1 });
  }
  const toggle = (kind: string) => {
    const next = new Set(picked);
    if (next.has(kind)) next.delete(kind);
    else next.add(kind);
    onPick(next);
  };
  const all = [...counts.keys()].every((kind) => picked.has(kind));
  const at = new Date(since);
  return (
    <div className={styles.since} role="region" aria-label="Changes since your last login">
      <span className={styles.dot} aria-hidden="true" />
      <span>
        <strong>Since your last login</strong> ({formatDate(at)},{" "}
        {at.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}):
      </span>
      {[...counts].map(([kind, { label, n }]) => (
        <button
          key={kind}
          type="button"
          className={styles.pill}
          aria-pressed={picked.has(kind)}
          onClick={() => toggle(kind)}
        >
          {n} {label}
        </button>
      ))}
      <button
        type="button"
        className={styles.show}
        onClick={() => onPick(all ? new Set() : new Set(counts.keys()))}
      >
        {all ? "Show the whole list" : `Show all ${latest.length}`}
      </button>
      <button
        type="button"
        className={styles.dismiss}
        onClick={onDismiss}
      >
        Dismiss
      </button>
    </div>
  );
};
